import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  FloatingPortal,
  FloatingNode,
  FloatingTree,
  useFloatingNodeId,
  useFloatingParentNodeId,
  type Placement,
} from '@floating-ui/react';
import { cloneElement, isValidElement, useState, type ReactElement, type ReactNode, type CSSProperties } from 'react';
import clsx from 'clsx';

export interface PopoverProps {
  children: ReactElement;
  content: (close: () => void) => ReactNode;
  placement?: Placement;
  offsetPx?: number;
  className?: string;
  width?: number | 'trigger';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  /** stop click propagation from the trigger (rows open the task on click) */
  stopPropagation?: boolean;
}

/**
 * Popovers form a floating-ui tree so a click inside a nested popover (submenu)
 * doesn't count as an outside press for its parent.
 */
export function Popover(props: PopoverProps) {
  const parentId = useFloatingParentNodeId();
  if (parentId === null) {
    return (
      <FloatingTree>
        <PopoverInner {...props} />
      </FloatingTree>
    );
  }
  return <PopoverInner {...props} />;
}

function PopoverInner({
  children,
  content,
  placement = 'bottom-start',
  offsetPx = 4,
  className,
  width,
  open: controlledOpen,
  onOpenChange,
  disabled,
  stopPropagation = true,
}: PopoverProps) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlledOpen ?? uncontrolled;
  const setOpen = (v: boolean) => {
    if (controlledOpen === undefined) setUncontrolled(v);
    onOpenChange?.(v);
  };

  const nodeId = useFloatingNodeId();
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    nodeId,
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    // position with top/left: the pop-in animation animates `transform`, which would otherwise
    // override the positioning transform and park the popover at the top-left until it ends
    transform: false,
    middleware: [
      offset(offsetPx),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, elements, rects }) {
          elements.floating.style.maxHeight = `${Math.max(160, availableHeight)}px`;
          if (width === 'trigger') elements.floating.style.width = `${rects.reference.width}px`;
        },
      }),
    ],
  });
  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context, { bubbles: false });
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  if (!isValidElement(children)) return children;
  const childProps = children.props as Record<string, unknown>;
  const trigger = cloneElement(children as ReactElement<Record<string, unknown>>, {
    ref: refs.setReference,
    'data-open': open ? 'true' : undefined,
    ...getReferenceProps({
      ...childProps,
      onClick: (e: React.MouseEvent) => {
        if (stopPropagation) e.stopPropagation();
        (childProps.onClick as ((e: React.MouseEvent) => void) | undefined)?.(e);
      },
    }),
  });

  const style: CSSProperties = { ...floatingStyles };
  if (typeof width === 'number') style.width = width;

  return (
    <FloatingNode id={nodeId}>
      {trigger}
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={style}
            className={clsx('popover', className)}
            data-floating=""
            data-positioned={isPositioned ? '' : undefined}
            {...getFloatingProps({ onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation() })}
          >
            {content(() => setOpen(false))}
          </div>
        </FloatingPortal>
      )}
    </FloatingNode>
  );
}
